import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { NotificationKindIcon } from './NotificationIcons'
import { placeAnchoredMenu } from '../utils/menuPlacement'
import './AdminHelpTip.css'

export default function AdminHelpTip({ label, children }) {
  const tipId = useId()
  const rootRef = useRef(null)
  const tipRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [spot, setSpot] = useState(null)

  // Le panneau d'administration est animé (transform) : un enfant en position
  // fixed s'y ancrerait au lieu de la fenêtre. L'infobulle est donc portée à la
  // racine et placée par le même utilitaire que les autres menus, qui borne
  // la position aux limites visibles.
  const place = useCallback(() => {
    const anchor = rootRef.current
    const tip = tipRef.current
    if (!anchor || !tip) return
    const trigger = anchor.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const width = Math.min(Math.ceil(tip.getBoundingClientRect().width) || 260, vw - 16)
    const height = Math.ceil(tip.getBoundingClientRect().height) || 96
    const { left, top } = placeAnchoredMenu({
      trigger,
      width,
      height,
      vw,
      vh,
      align: 'start',
      gap: 8,
    })
    setSpot({ left, top, width })
  }, [])

  useLayoutEffect(() => {
    if (!open) {
      setSpot(null)
      return undefined
    }
    place()
    const onChange = () => place()
    window.addEventListener('resize', onChange)
    window.addEventListener('scroll', onChange, true)
    return () => {
      window.removeEventListener('resize', onChange)
      window.removeEventListener('scroll', onChange, true)
    }
  }, [open, place])

  useEffect(() => {
    if (!open) return undefined
    const onPointer = (event) => {
      if (rootRef.current?.contains(event.target)) return
      if (tipRef.current?.contains(event.target)) return
      setOpen(false)
    }
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const tip = (
    <span
      id={tipId}
      ref={tipRef}
      className={`admin-help-tip${open ? ' is-open' : ''}`}
      role="tooltip"
      style={spot ? { left: `${spot.left}px`, top: `${spot.top}px`, width: `${spot.width}px` } : undefined}
    >
      {children}
    </span>
  )

  const finePointer = () => typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches

  return (
    <span
      className="admin-help-wrap"
      ref={rootRef}
      onMouseEnter={() => { if (finePointer()) setOpen(true) }}
      onMouseLeave={() => { if (finePointer()) setOpen(false) }}
    >
      <button
        type="button"
        className="admin-help"
        aria-label={label}
        aria-describedby={open ? tipId : undefined}
        aria-expanded={open}
        onPointerDown={(event) => {
          event.preventDefault()
          setOpen((current) => !current)
        }}
        onFocus={() => setOpen(true)}
        onBlur={(event) => {
          if (!rootRef.current?.contains(event.relatedTarget)) setOpen(false)
        }}
      >
        <NotificationKindIcon kind="info" size={16} />
      </button>
      {open && typeof document !== 'undefined' ? createPortal(tip, document.body) : null}
    </span>
  )
}
